import { outcomeColors } from '../constants';
import { clamp, getKeeperOrigin, getPreviewTarget, getShotBand, projectPoint } from '../helpers';
import { RenderProfile } from '../quality';
import { EngineState, Phase, ShotResolution, Vec3 } from '../types';

const bandTones = {
  red: {
    beam: 'rgba(248,113,113,0.36)',
    glow: 'rgba(248,113,113,0.22)',
    stroke: 'rgba(254,202,202,0.96)',
  },
  yellow: {
    beam: 'rgba(251,191,36,0.34)',
    glow: 'rgba(251,191,36,0.2)',
    stroke: 'rgba(254,240,138,0.94)',
  },
  green: {
    beam: 'rgba(52,211,153,0.34)',
    glow: 'rgba(52,211,153,0.22)',
    stroke: 'rgba(209,250,229,0.96)',
  },
} as const;

const fillPanelGlow = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  glowColor: string,
) => {
  const glow = ctx.createRadialGradient(x, y, 0, x, y, radius);
  glow.addColorStop(0, glowColor);
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
};

export const drawPreviewTargets = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  phase: Phase,
  aim: number,
  power: number,
  nowMs: number,
  profile: RenderProfile,
  pass: 'guides' | 'reticle' | 'all' = 'all',
) => {
  if (phase !== 'aim') {
    return;
  }

  const band = getShotBand(power);
  const tone = bandTones[band];
  const activeTarget = projectPoint(getPreviewTarget(aim, power), width, height);

  ctx.save();
  if (pass !== 'reticle') {
    const origin = projectPoint({ x: 0, y: 0.02, z: 11.7 }, width, height);
    const targetBox = {
      lowLeft: projectPoint(getPreviewTarget(0, 0.1), width, height),
      lowRight: projectPoint(getPreviewTarget(1, 0.1), width, height),
      highRight: projectPoint(getPreviewTarget(1, 0.96), width, height),
      highLeft: projectPoint(getPreviewTarget(0, 0.96), width, height),
    };
    const targetMidLeft = projectPoint(getPreviewTarget(0, 0.53), width, height);
    const targetMidRight = projectPoint(getPreviewTarget(1, 0.53), width, height);
    const targetCenterLow = projectPoint(getPreviewTarget(0.5, 0.1), width, height);
    const targetCenterHigh = projectPoint(getPreviewTarget(0.5, 0.96), width, height);
    const beam = ctx.createLinearGradient(origin.x, origin.y, activeTarget.x, activeTarget.y);
    beam.addColorStop(0, 'rgba(255,255,255,0)');
    beam.addColorStop(0.45, tone.beam);
    beam.addColorStop(1, tone.stroke);

    ctx.beginPath();
    ctx.moveTo(targetBox.lowLeft.x, targetBox.lowLeft.y);
    ctx.lineTo(targetBox.lowRight.x, targetBox.lowRight.y);
    ctx.lineTo(targetBox.highRight.x, targetBox.highRight.y);
    ctx.lineTo(targetBox.highLeft.x, targetBox.highLeft.y);
    ctx.closePath();
    const targetField = ctx.createLinearGradient(targetBox.lowLeft.x, targetBox.lowLeft.y, targetBox.highLeft.x, targetBox.highLeft.y);
    targetField.addColorStop(0, 'rgba(15,23,42,0.08)');
    targetField.addColorStop(1, 'rgba(15,23,42,0.2)');
    ctx.fillStyle = targetField;
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(targetBox.lowLeft.x, targetBox.lowLeft.y);
    ctx.lineTo(targetBox.lowRight.x, targetBox.lowRight.y);
    ctx.lineTo(targetBox.highRight.x, targetBox.highRight.y);
    ctx.lineTo(targetBox.highLeft.x, targetBox.highLeft.y);
    ctx.closePath();
    ctx.strokeStyle = 'rgba(191,219,254,0.22)';
    ctx.lineWidth = 1.15;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(targetMidLeft.x, targetMidLeft.y);
    ctx.lineTo(targetMidRight.x, targetMidRight.y);
    ctx.moveTo(targetCenterLow.x, targetCenterLow.y);
    ctx.lineTo(targetCenterHigh.x, targetCenterHigh.y);
    ctx.strokeStyle = 'rgba(191,219,254,0.16)';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.beginPath();
    ctx.strokeStyle = beam;
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.moveTo(origin.x, origin.y);
    ctx.lineTo(activeTarget.x, activeTarget.y);
    ctx.stroke();
  }

  const pulse = profile.animateTarget ? 1 + Math.sin(nowMs / 84) * 0.14 : 1;
  const outerRadius = Math.max(13, activeTarget.scale * 0.72 * pulse);
  const innerRadius = outerRadius * 0.48;
  const bracketInset = outerRadius * 0.44;
  const bracketLength = outerRadius * 0.38;

  if (pass !== 'guides' && profile.useGlow) {
    fillPanelGlow(ctx, activeTarget.x, activeTarget.y, outerRadius * 2.4, tone.glow);
  }

  if (pass !== 'guides') {
    ctx.beginPath();
    ctx.strokeStyle = tone.stroke;
    ctx.lineWidth = 2.4;
    ctx.arc(activeTarget.x, activeTarget.y, outerRadius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.beginPath();
    ctx.strokeStyle = 'rgba(255,255,255,0.88)';
    ctx.lineWidth = 1.2;
    ctx.arc(activeTarget.x, activeTarget.y, innerRadius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.beginPath();
    ctx.lineWidth = 2;
    ctx.strokeStyle = tone.stroke;
    ctx.moveTo(activeTarget.x - outerRadius, activeTarget.y - bracketInset);
    ctx.lineTo(activeTarget.x - outerRadius, activeTarget.y - bracketInset - bracketLength);
    ctx.lineTo(activeTarget.x - bracketInset, activeTarget.y - outerRadius);
    ctx.moveTo(activeTarget.x + outerRadius, activeTarget.y - bracketInset);
    ctx.lineTo(activeTarget.x + outerRadius, activeTarget.y - bracketInset - bracketLength);
    ctx.lineTo(activeTarget.x + bracketInset, activeTarget.y - outerRadius);
    ctx.moveTo(activeTarget.x - outerRadius, activeTarget.y + bracketInset);
    ctx.lineTo(activeTarget.x - outerRadius, activeTarget.y + bracketInset + bracketLength);
    ctx.lineTo(activeTarget.x - bracketInset, activeTarget.y + outerRadius);
    ctx.moveTo(activeTarget.x + outerRadius, activeTarget.y + bracketInset);
    ctx.lineTo(activeTarget.x + outerRadius, activeTarget.y + bracketInset + bracketLength);
    ctx.lineTo(activeTarget.x + bracketInset, activeTarget.y + outerRadius);
    ctx.stroke();

    ctx.beginPath();
    ctx.fillStyle = tone.stroke;
    ctx.arc(activeTarget.x, activeTarget.y, Math.max(3, innerRadius * 0.2), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
};

export const drawBall = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  ball: Vec3,
  flightProgress: number,
  shotPower: number | undefined,
  isFlight: boolean,
  profile: RenderProfile,
) => {
  const projected = projectPoint(ball, width, height);
  const radius = Math.max(5.2, projected.scale * 0.145);
  const band = shotPower !== undefined ? getShotBand(shotPower) : 'yellow';
  const tone = bandTones[band];

  const shadowLift = Math.max(0, ball.y) * projected.scale * 0.18;
  ctx.beginPath();
  ctx.fillStyle = 'rgba(2,6,23,0.28)';
  ctx.ellipse(projected.x, projected.y + radius * 0.92 + shadowLift, radius * 0.82, radius * 0.34, 0, 0, Math.PI * 2);
  ctx.fill();

  if (isFlight && profile.quality !== 'reduced') {
    const trailLength = radius * (3.8 + flightProgress * 2.8);
    const trail = ctx.createLinearGradient(projected.x - trailLength, projected.y + radius * 0.68, projected.x, projected.y);
    trail.addColorStop(0, 'rgba(255,255,255,0)');
    trail.addColorStop(0.6, tone.beam);
    trail.addColorStop(1, tone.stroke);

    ctx.beginPath();
    ctx.strokeStyle = trail;
    ctx.lineWidth = Math.max(2.2, radius * 0.52);
    ctx.lineCap = 'round';
    ctx.moveTo(projected.x - trailLength, projected.y + radius * 0.62);
    ctx.lineTo(projected.x, projected.y);
    ctx.stroke();

    ctx.beginPath();
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = Math.max(1.1, radius * 0.22);
    ctx.moveTo(projected.x - trailLength * 0.82, projected.y + radius * 0.4);
    ctx.lineTo(projected.x - radius * 0.18, projected.y - radius * 0.1);
    ctx.stroke();
  }

  if (profile.useGlow) {
    fillPanelGlow(ctx, projected.x, projected.y, radius * (isFlight ? 3.2 : 2.1), isFlight ? tone.glow : 'rgba(191,219,254,0.12)');
  }

  const ballShade = ctx.createRadialGradient(projected.x - radius * 0.35, projected.y - radius * 0.45, radius * 0.2, projected.x, projected.y, radius);
  ballShade.addColorStop(0, '#ffffff');
  ballShade.addColorStop(1, '#e2e8f0');
  ctx.beginPath();
  ctx.fillStyle = ballShade;
  ctx.arc(projected.x, projected.y, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.fillStyle = 'rgba(255,255,255,0.28)';
  ctx.arc(projected.x - radius * 0.3, projected.y - radius * 0.36, radius * 0.2, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.fillStyle = '#0f172a';
  ctx.arc(projected.x - radius * 0.22, projected.y - radius * 0.2, radius * 0.24, 0, Math.PI * 2);
  ctx.arc(projected.x + radius * 0.3, projected.y - radius * 0.06, radius * 0.22, 0, Math.PI * 2);
  ctx.arc(projected.x, projected.y + radius * 0.34, radius * 0.22, 0, Math.PI * 2);
  ctx.fill();
};

export const drawGoalPulse = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  target: Vec3 | null,
  pulse: number,
  profile: RenderProfile,
) => {
  if (!target || pulse <= 0) {
    return;
  }

  const projected = projectPoint(target, width, height);
  const radius = Math.max(projected.scale * (0.85 + pulse * 0.4), 22);
  if (profile.useGlow) {
    const glow = ctx.createRadialGradient(projected.x, projected.y, 0, projected.x, projected.y, radius * 1.4);
    glow.addColorStop(0, `rgba(110,231,183,${0.28 * pulse})`);
    glow.addColorStop(0.4, `rgba(196,181,253,${0.18 * pulse})`);
    glow.addColorStop(1, 'rgba(196,181,253,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(projected.x - radius * 1.6, projected.y - radius * 1.6, radius * 3.2, radius * 3.2);
  }

  ctx.beginPath();
  ctx.strokeStyle = `rgba(255,255,255,${0.5 * pulse})`;
  ctx.lineWidth = 2;
  ctx.arc(projected.x, projected.y, radius, 0, Math.PI * 2);
  ctx.stroke();
};

export const drawOutcomeWave = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  shot: ShotResolution | null,
  progress: number,
  profile: RenderProfile,
) => {
  if (!shot) {
    return;
  }

  const intensity = clamp(1 - progress, 0, 1);
  if (intensity <= 0) {
    return;
  }

  const burstOrigin = shot.outcome === 'save' ? getKeeperOrigin(shot.keeperLane, shot.target.y) : shot.target;
  const projected = projectPoint(burstOrigin, width, height);
  const colors = outcomeColors[shot.outcome];
  const baseRadius = Math.max(26, projected.scale * (shot.outcome === 'goal' ? 1.12 : 0.94));

  ctx.save();
  if (profile.quality === 'full') {
    ctx.globalCompositeOperation = 'screen';
  }
  if (profile.useGlow) {
    const glow = ctx.createRadialGradient(projected.x, projected.y, 0, projected.x, projected.y, baseRadius * (2.2 + (1 - intensity) * 0.8));
    glow.addColorStop(0, `${colors[0]}${Math.round(intensity * 76).toString(16).padStart(2, '0')}`);
    glow.addColorStop(0.45, `${colors[1]}${Math.round(intensity * 64).toString(16).padStart(2, '0')}`);
    glow.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(projected.x - baseRadius * 2.8, projected.y - baseRadius * 2.8, baseRadius * 5.6, baseRadius * 5.6);
  }

  ctx.beginPath();
  ctx.strokeStyle = `${colors[0]}${Math.round(intensity * 210).toString(16).padStart(2, '0')}`;
  ctx.lineWidth = shot.outcome === 'goal' ? 3.4 : 2.4;
  ctx.arc(projected.x, projected.y, baseRadius * (1.12 + (1 - intensity) * 0.9), 0, Math.PI * 2);
  ctx.stroke();

  if (shot.outcome === 'goal') {
    ctx.beginPath();
    ctx.strokeStyle = `${colors[2]}${Math.round(intensity * 188).toString(16).padStart(2, '0')}`;
    ctx.lineWidth = shot.perfect ? 2.6 : 1.8;
    ctx.arc(projected.x, projected.y, baseRadius * (0.72 + (1 - intensity) * 0.58), 0, Math.PI * 2);
    ctx.stroke();
  }

  if (shot.outcome === 'save') {
    ctx.beginPath();
    ctx.lineCap = 'round';
    ctx.strokeStyle = `${colors[2]}${Math.round(intensity * 220).toString(16).padStart(2, '0')}`;
    ctx.lineWidth = 2.8;
    ctx.moveTo(projected.x - baseRadius * 0.86, projected.y - baseRadius * 0.34);
    ctx.lineTo(projected.x + baseRadius * 0.92, projected.y + baseRadius * 0.28);
    ctx.moveTo(projected.x - baseRadius * 0.1, projected.y - baseRadius * 0.94);
    ctx.lineTo(projected.x + baseRadius * 0.56, projected.y + baseRadius * 0.82);
    ctx.stroke();
  }

  if (shot.outcome === 'miss') {
    ctx.beginPath();
    ctx.ellipse(projected.x, projected.y, baseRadius * 0.92, baseRadius * 0.52, -0.34, 0, Math.PI * 2);
    ctx.strokeStyle = `${colors[1]}${Math.round(intensity * 204).toString(16).padStart(2, '0')}`;
    ctx.lineWidth = 2.1;
    ctx.stroke();
  }
  ctx.restore();
};

export const drawParticles = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  engine: EngineState,
  profile: RenderProfile,
) => {
  if (profile.particleLimit <= 0) {
    return;
  }

  ctx.save();
  if (profile.quality === 'full') {
    ctx.globalCompositeOperation = 'screen';
  }
  engine.particles.slice(0, profile.particleLimit).forEach((particle) => {
    const projected = projectPoint(particle.position, width, height);
    const alpha = 1 - particle.lifeMs / particle.maxLifeMs;
    ctx.beginPath();
    ctx.fillStyle = `${particle.color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
    if (profile.useGlow) {
      ctx.shadowColor = particle.color;
      ctx.shadowBlur = alpha * 16;
    }
    ctx.arc(projected.x, projected.y, Math.max(1.8, projected.scale * particle.radius * alpha), 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
  });
  ctx.restore();
};

export const drawKickFlash = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  phase: Phase,
  phaseElapsedMs: number,
  profile: RenderProfile,
) => {
  if (phase !== 'flight' || phaseElapsedMs > 180 || profile.quality === 'reduced') {
    return;
  }

  const progress = clamp(phaseElapsedMs / 180, 0, 1);
  const origin = projectPoint({ x: -0.03, y: 0.28, z: 12.42 }, width, height);
  const radius = Math.max(8, origin.scale * 0.22) * (1 + progress * 1.6);
  ctx.beginPath();
  ctx.strokeStyle = `rgba(254,240,138,${0.78 * (1 - progress)})`;
  ctx.lineWidth = profile.quality === 'full' ? 2.4 : 1.6;
  ctx.arc(origin.x, origin.y, radius, 0, Math.PI * 2);
  ctx.stroke();
};

export const drawNetRipple = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  shot: ShotResolution | null,
  progress: number,
  profile: RenderProfile,
) => {
  if (!shot || shot.outcome !== 'goal' || progress <= 0 || progress >= 0.82) {
    return;
  }

  const target = projectPoint(shot.target, width, height);
  const intensity = 1 - progress / 0.82;
  const rings = profile.quality === 'full' ? 3 : 1;
  for (let index = 0; index < rings; index += 1) {
    const radius = Math.max(15, target.scale * 0.5) * (1 + progress * (1.5 + index * 0.32));
    ctx.beginPath();
    ctx.strokeStyle = `rgba(226,232,240,${Math.max(0, intensity * (0.46 - index * 0.1))})`;
    ctx.lineWidth = 1.4;
    ctx.ellipse(target.x, target.y, radius, radius * 0.56, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
};
